/**
 * Split a continuous recording into one scene per speaker turn, derived
 * from the transcript's speaker labels. No per-scene crop, no special
 * layout — reuses the existing `CreateScenesByMediaTimeCommand` so anchor
 * resolution, scene replacement, validation, and undo are all inherited.
 */

import { CreateScenesByMediaTimeCommand } from '@src/editor/commands/CreateScenesByMediaTimeCommand';

import { buildSpeakerScenes } from './buildSpeakerTurns';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { VideoEditor } from '@src/editor/VideoEditor';

export interface CreateScenesPerSpeakerInput {
  editor: IVideoEditor;
  /**
   * Shot detections in media time. When given, scenes are also split at each
   * camera cut so no scene spans two shots (see `buildSpeakerScenes`).
   */
  shots?: ReadonlyArray<SceneDetection> | null;
  /**
   * Keyed by `VideoElement.source` (URL). When given, each scene starts with a
   * cover crop instead of full-frame — what a scene no framing reaches keeps.
   */
  naturalMediaDimensions?: Record<string, NaturalDimensions>;
}

export function createScenesPerSpeaker({
  editor,
  shots,
  naturalMediaDimensions,
}: CreateScenesPerSpeakerInput): boolean {
  const transcript = editor.getOriginalTranscript();
  if (!transcript || transcript.length === 0) return false;

  const sceneDetections = buildSpeakerScenes(transcript, shots ?? []);
  if (sceneDetections.length === 0) return false;

  // `CreateScenesByMediaTimeCommand` is typed against the concrete
  // `VideoEditor`; the runtime instance reaching this function is the same
  // object, just exposed through the `IVideoEditor` interface to callers.
  const command = new CreateScenesByMediaTimeCommand(
    editor as VideoEditor,
    sceneDetections,
    naturalMediaDimensions,
  );
  return editor.executeCommand(command);
}
